import { GenericStatusByCall } from '../../enums/status-by-call';
import { BasePagedResponse, PagedResponse, SimplePagedResponse } from '../../models/common-response-types';
import { Company } from '../../models/company';
import {
  AvailableIndexesResponse,
  CompanyLogoAndNameState,
  DividendPayerData,
  Earning,
  EpicMoversRequestAndResponse,
  ETF,
  GapperResponse,
  HighGrowthCompanyData,
  NewStockData,
  PageableFilterRequest,
  PopularStock,
  RatiosResponse,
  Sector,
  SectorDetail,
  SymbolStatementApiResponse,
  Week52HighLowResponse,
  Week52HighLowStatusType,
  Week52HighLowTopListResponse,
  MarketCapsRequestAndResponse,
} from '../../models/fundamental/types';
import { StockDescriptor } from '../../models/stock-descriptor';
import { SymbolKeyStat } from '../../models/symbol-key-stat';
import { DynamicFilterType } from '../../services/filter/types';
import { TypedObject } from '../../util/types';

export type GetSectorsResponse = Sector & {symbolUniverseSymbolCount: number};

export interface FundamentalState {
  marketCapsData: MarketCapsRequestAndResponse;
  stockQuery: string;
  stockResults: StockDescriptor[];
  symbol: string;
  keyStats: SymbolKeyStat[];
  // TODO: Stop using companies for getting CompanyName. Take it from companyLogoAndName
  companies: Company[];
  companyLogoAndName: TypedObject<CompanyLogoAndNameState>;
  error: any;
  popularStocks: PopularStock[];
  newStocks: NewStockData[];
  dividendPayers: PagedResponse<DividendPayerData>;
  highGrowthCompanies: PagedResponse<HighGrowthCompanyData>;
  sectors: Sector[];
  sectorDetails: SectorDetail;
  availableIndexes: AvailableIndexesResponse[];
  marketIndexes: [];
  earningsSearchResponse: PagedResponse<Earning>;
  previousEarnings: Record<string, Earning[]>;
  earningsCalendarSearchState: EarningsCalendarSearchState;
  statusByCall: GenericStatusByCall<FundamentalCall>;
  ETFData: BasePagedResponse<ETF>;
  statementsData: SymbolStatementApiResponse;
  gappersData: SimplePagedResponse<GapperResponse>;
  week52High: SimplePagedResponse<Week52HighLowResponse>;
  week52Low: SimplePagedResponse<Week52HighLowResponse>;
  week52Direction: Week52HighLowStatusType;
  week52HighLowTopList: Week52HighLowTopListResponse;
  ratiosData: RatiosResponse;
  epicMoversData: EpicMoversRequestAndResponse;
}

export type SearchEarningsPayload = PageableFilterRequest & {
  symbols?: string[];
  startDate?: string | Date;
  endDate?: string | Date;
  /** Accepted so the shared helper can build one payload shape; this app sends none. */
  symbologies?: string[];
};

export type EarningsCalendarRangeState = {
  startDate: string;
  endDate: string;
  key: string;
};

export type EarningsCalendarLastFetchedQuery = {
  startDate: string;
  endDate: string;
  symbols: string[];
};

export enum EarningsSymbolFilterEnum {
  AllSymbols = 'ALL_SYMBOLS',
  MyPortfolio = 'MY_PORTFOLIO',
  FavoritesWatchlist = 'FAVORITES_WATCHLIST',
  BySymbol = 'BY_SYMBOL',
}

export type EarningsSymbolFilterId = EarningsSymbolFilterEnum;

export type EarningsCalendarSearchState = {
  range: EarningsCalendarRangeState | null;
  searchValue: string;
  activeSymbols: string[];
  lastFetchedQuery: EarningsCalendarLastFetchedQuery | null;
  // INVARIANT: every write of activeSymbols must also write selectedSymbolFilterId -
  // the earnings calendar dropdown label is derived from this pair, so letting one
  // drift from the other shows a filter name that doesn't match the shown results.
  selectedSymbolFilterId: EarningsSymbolFilterId | null;
};

export type GetPreviousEarningsPayload = {
  symbol: string;
  /** Date of the selected earnings event - only reports strictly before it are fetched. */
  beforeDate: string;
};

export type SearchStockPayload = {
  limit: number;
  query: string;
  params: DynamicFilterType;
};

export enum FundamentalCall {
  getPopularStocks,
  getNewStocks,
  getDividendPayers,
  getHighGrowthCompanies,
  getSectors,
  getSectorDetails,
  getIPOs,
  getEarnings,
  searchEarnings,
  getPreviousEarnings,
  getSplits,
  getCompanyLogoAndName,
  searchStocks,
  getMarketIndexes,
  getETFs,
  getETFDetails,
  getStatements,
  getStatementsCashFlow,
  getStatementsBalanceSheet,
  getStatementsIncome,
  getWeek52HighLow,
  getWeek52HighLowTopList,
  getGappers,
  getAllRatios,
  getRatiosLiquidityAndSolvency,
  getRatiosProfitability,
  getRatiosValuation,
  getEpicMovers,
  getMarketCaps,
  getAvailableIndexes,
  getETFInfo,
  getETFHolders,
  getETFWeightings,
  getNonWorkingDaysAndMarketTime,
  getEstimates,
  getEstimatesSummary,
  getCurrencies,
  getMarketPressure,
  getEpicMoversCustom,
}

export enum SectorEnum {
  CommunicationsServices = 'Communication Services',
  HealthCare = 'Health Care',
  ConsumerStaples = 'Consumer Staples',
  Technology = 'Technology',
  Utilities = 'Utilities',
  ConsumerDiscretionary = 'Consumer Discretionary',
  Financials = 'Financials',
  Materials = 'Materials',
  RealEstate = 'Real Estate',
  Industrials = 'Industrials',
  Energy = 'Energy',
  ETFs = 'ETFs',
  Gappers = 'Gappers',
}
